import React, { useState, useEffect, useRef } from 'react';
import { AnnualPlanExportFilters, Plan, FilterOptions, TimePeriodData } from '@shared/schema';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import AnnualPlanFilters from './AnnualPlanFilters.tsx';
import AnnualPlanTable from './AnnualPlanTable.tsx';
import OutOfTimeDecisionDialog from './Shared/OutOfTimeDecisionDialog';
import { useTranslation } from '@/core/i18n/i18n';
import { appendScopeToApiUrl } from '@/lib/queryClient';
import { OutOfTimeRequestStatusState } from '@/core/types/state';
import { useAnyLoading } from '@/core/useAnyLoading';

interface AnnualPlanningProps {
  plans: Plan[];
  filterOptions: FilterOptions;
  isLoadingPlans: boolean;
  errorMessagePlans: string | null;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  getPaginatedPlans: (
    page: number,
    pageSize: number,
    sortBy?: string,
    sortOrder?: string,
    filters?: Record<string, string>,
  ) => void;
  exportAnnualPlansToCSV: (filters?: AnnualPlanExportFilters) => void;
  exportAnnualPlansToExcel: (filters?: AnnualPlanExportFilters) => void;
  periods: TimePeriodData[];
  scopeName: string;
  setCurrentVersion: (versionId: number | null) => void;
  setFulfillmentVersion: (versionId: number | null) => void;
  resetActivitiesState: () => void;
  resetChecklistState: () => void;
  resetFocusState: () => void;
  resetObjectivesState: () => void;
  resetRequirementsState: () => void;
  resetPlansState: () => void;
  setReadOnlyMode: (readOnly: boolean) => void;
  setCreatingOutOfTimePlan: (creatingOutOfTime: boolean, requestId?: number | null) => void;
  setApYear: (year: number | null) => void;
  setCurrentBusinessUnit: (businessUnit: string | null) => void;
  deleteAnnualPlan: (id: number) => void;
  setCurrentPlanId: (planId: number | null) => void;
  setSelectedHierarchicalModel: (hierarchicalModelId: number | null) => void;
  reopenPlanVersion?: (versionId: number) => void;
  reopenFulfillmentVersion?: (fulfillmentVersionId: number) => void;
  anularPlanVersion: (apId: number) => void;
  returnPlanToDefinition: (apId: number) => Promise<void> | void;
  userRoles: string[];
  loadingUserRoles: boolean;
  outOfTimeRequestStatus: OutOfTimeRequestStatusState | null;
  loadingOutOfTimeRequestStatus: boolean;
  submittingOutOfTimeRequest: boolean;
  processingOutOfTimeDecision: boolean;
  processingReopenDecision: boolean;
  fetchCurrentUserRoles: () => void;
  fetchOutOfTimeRequestStatus: () => void;
  submitOutOfTimeRequest: (reason: string) => Promise<void>;
  processOutOfTimeRequestDecision: (params: {
    requestId: number;
    decision: 'accept' | 'reject';
    token: string;
    rejectionReason?: string;
  }) => Promise<void>;
}

const AnnualPlanning: React.FC<AnnualPlanningProps> = (props) => {
  const {
    plans,
    periods,
    filterOptions: propsFilterOptions,
    currentPage,
    totalPages,
    pageSize,
    getPaginatedPlans,
    exportAnnualPlansToCSV,
    exportAnnualPlansToExcel,
    setCurrentVersion,
    setFulfillmentVersion,
    resetActivitiesState,
    resetChecklistState,
    resetFocusState,
    resetObjectivesState,
    resetRequirementsState,
    resetPlansState,
    setReadOnlyMode,
    setCreatingOutOfTimePlan,
    setApYear,
    setCurrentBusinessUnit,
    deleteAnnualPlan,
    setCurrentPlanId,
    setSelectedHierarchicalModel,
    reopenPlanVersion,
    reopenFulfillmentVersion,
    anularPlanVersion,
    returnPlanToDefinition,
    userRoles,
    loadingUserRoles,
    outOfTimeRequestStatus,
    loadingOutOfTimeRequestStatus,
    submittingOutOfTimeRequest,
    processingOutOfTimeDecision,
    processingReopenDecision,
    fetchCurrentUserRoles,
    fetchOutOfTimeRequestStatus,
    submitOutOfTimeRequest,
    processOutOfTimeRequestDecision,
  } = props;
  const isAnyLoading = useAnyLoading();
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    hierarchicalModels: [],
    status: [],
    years: [],
  });
  const [reopenTokenInfo, setReopenTokenInfo] = useState<{
    versionId?: number;
    fulfillmentVersionId?: number;
    action: string;
    raw: string;
  } | null>(null);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [outOfTimeTokenInfo, setOutOfTimeTokenInfo] = useState<{
    requestId: number;
    raw: string;
  } | null>(null);
  const [showOutOfTimeModal, setShowOutOfTimeModal] = useState(false);
  const [outOfTimeRejectionReason, setOutOfTimeRejectionReason] = useState('');
  const [filters, setFilters] = useState({
    hierarchicalModelId: '',
    status: '',
    year: '',
    fromDate: '',
    toDate: '',
    skipDefaultYear: '',
  });
  const userRolesRequestedRef = useRef(false);
  const defaultYearAppliedRef = useRef(false);

  useEffect(() => {
    resetActivitiesState();
    resetChecklistState();
    resetFocusState();
    resetObjectivesState();
    resetRequirementsState();
    setCurrentVersion(null);
    resetPlansState();

    getPaginatedPlans(1, 10);
  }, []);

  useEffect(() => {
    if (userRolesRequestedRef.current) return;
    if (!loadingUserRoles && userRoles.length === 0) {
      userRolesRequestedRef.current = true;
      fetchCurrentUserRoles();
    }
  }, [fetchCurrentUserRoles, loadingUserRoles, userRoles.length]);

  useEffect(() => {
    if (!showOutOfTimeModal) {
      setOutOfTimeRejectionReason('');
    }
  }, [showOutOfTimeModal]);

  const handlePageChange = (page: number) => {
    getPaginatedPlans(page, pageSize, undefined, undefined, filters);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    getPaginatedPlans(1, newPageSize, undefined, undefined, filters);
  };

  const reloadPage = () => {
    getPaginatedPlans(currentPage, pageSize, undefined, undefined, filters);
  };

  useEffect(() => {
    const loadData = () => {
      const hierarchicalModels = [
        { HierarchicalModelId: 0, Designation: t('common.all'), ReportsTo: 0, BossId: 0 },
        ...Array.from(new Set(propsFilterOptions?.hierarchicalModels || [])),
      ];
      const status = [t('common.all'), ...Array.from(new Set(propsFilterOptions?.status || []))];

      const years = [t('common.all'), ...Array.from(new Set(propsFilterOptions?.years || []))];

      setFilterOptions({
        hierarchicalModels,
        status: status as string[],
        years: years as string[],
      });
    };
    if (plans.length > 0) {
      loadData();
    }
  }, [
    plans,
    propsFilterOptions?.hierarchicalModels,
    propsFilterOptions?.status,
    propsFilterOptions?.years,
    t,
  ]);

  useEffect(() => {
    if (defaultYearAppliedRef.current) return;
    const availableYears = (propsFilterOptions?.years || []).map((year) => String(year));
    if (!availableYears.length) return;
    const currentYear = String(new Date().getFullYear());
    defaultYearAppliedRef.current = true;
    if (filters.skipDefaultYear || filters.year !== '' || !availableYears.includes(currentYear)) {
      return;
    }
    const nextFilters = { ...filters, year: currentYear };
    setFilters(nextFilters);
    getPaginatedPlans(1, pageSize, undefined, undefined, nextFilters);
  }, [filters, getPaginatedPlans, pageSize, propsFilterOptions?.years]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const apToken = params.get('ap_token');
    if (!apToken) return;
    const parts = apToken.split('.');
    if (parts.length !== 3) return;
    try {
      const middle = parts[1];
      if (!middle) return;
      const json = atob(middle.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(json);
      if (payload.action === 'reopenDefinitions' && typeof payload.versionId === 'number') {
        setReopenTokenInfo({ versionId: payload.versionId, action: payload.action, raw: apToken });
        setShowReopenModal(true);
      } else if (
        payload.action === 'reopenSelfAssessment' &&
        typeof payload.fulfillmentVersionId === 'number'
      ) {
        setReopenTokenInfo({
          fulfillmentVersionId: payload.fulfillmentVersionId,
          action: payload.action,
          raw: apToken,
        });
        setShowReopenModal(true);
      } else if (
        payload.action === 'outOfTimePlanRequest' &&
        typeof payload.requestId === 'number'
      ) {
        setOutOfTimeTokenInfo({ requestId: payload.requestId, raw: apToken });
        setShowOutOfTimeModal(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleDecision = async (decision: 'accept' | 'reject') => {
    if (!reopenTokenInfo) return;
    try {
      let endpoint = '';
      if (reopenTokenInfo.action === 'reopenDefinitions' && reopenTokenInfo.versionId) {
        endpoint = `/api/annual-plans/versions/${reopenTokenInfo.versionId}/reopen/decision`;
      } else if (
        reopenTokenInfo.action === 'reopenSelfAssessment' &&
        reopenTokenInfo.fulfillmentVersionId
      ) {
        endpoint = `/api/annual-plans/fulfillment-versions/${reopenTokenInfo.fulfillmentVersionId}/reopen/decision`;
      } else {
        return;
      }
      await fetch(appendScopeToApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, token: reopenTokenInfo.raw }),
      });
      setShowReopenModal(false);
      reloadPage();
    } catch (e) {
      console.error('Error sending reopen decision', e);
      setShowReopenModal(false);
    }
  };

  const handleOutOfTimeDecision = async (decision: 'accept' | 'reject') => {
    if (!outOfTimeTokenInfo) return;
    if (decision === 'reject' && !trimmedOutOfTimeRejectionReason) {
      return;
    }
    try {
      const decisionPayload = {
        requestId: outOfTimeTokenInfo.requestId,
        decision,
        token: outOfTimeTokenInfo.raw,
        ...(decision === 'reject' ? { rejectionReason: trimmedOutOfTimeRejectionReason } : {}),
      };

      await processOutOfTimeRequestDecision(decisionPayload);
      reloadPage();
    } catch (e) {
      console.error('Error sending out-of-time decision', e);
    } finally {
      setShowOutOfTimeModal(false);
      setOutOfTimeRejectionReason('');
    }
  };

  const handleFilterChange = () => {
    getPaginatedPlans(1, pageSize, undefined, undefined, filters);
  };

  const handleClearFilters = () => {
    getPaginatedPlans(1, pageSize, undefined, undefined, { skipDefaultYear: '1' });
  };

  const trimmedOutOfTimeRejectionReason = outOfTimeRejectionReason.trim();
  const activeExportFilters: AnnualPlanExportFilters = {
    ...(filters.hierarchicalModelId ? { hierarchicalModelId: filters.hierarchicalModelId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.year ? { year: filters.year } : {}),
    ...(filters.fromDate ? { fromDate: filters.fromDate } : {}),
    ...(filters.toDate ? { toDate: filters.toDate } : {}),
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: 4 }}>
      {/* Content */}
      <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
        {/* Breadcrumb */}
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary', fontSize: '1rem' }}
        >
          {t('filters.filters')}
        </Typography>

        {/* Filters */}
        <AnnualPlanFilters
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          isAnyLoading={isAnyLoading}
          filters={filters}
          setFilters={setFilters}
        />

        {/* Table */}
        <AnnualPlanTable
          plans={plans}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          filterOptions={filterOptions}
          exportAnnualPlansToCSV={() => exportAnnualPlansToCSV(activeExportFilters)}
          exportAnnualPlansToExcel={() => exportAnnualPlansToExcel(activeExportFilters)}
          setCurrentVersion={setCurrentVersion}
          setFulfillmentVersion={setFulfillmentVersion}
          setReadOnlyMode={setReadOnlyMode}
          setCreatingOutOfTimePlan={setCreatingOutOfTimePlan}
          periods={periods}
          setApYear={setApYear}
          setCurrentBusinessUnit={setCurrentBusinessUnit}
          isAnyLoading={isAnyLoading}
          deleteAnnualPlan={deleteAnnualPlan}
          setCurrentPlanId={setCurrentPlanId}
          setSelectedHierarchicalModel={setSelectedHierarchicalModel}
          reopenPlanVersion={reopenPlanVersion as (versionId: number, reason: string) => void}
          reopenFulfillmentVersion={
            reopenFulfillmentVersion as (fulfillmentVersionId: number, reason: string) => void
          }
          reloadPage={reloadPage}
          anularPlanVersion={anularPlanVersion}
          returnPlanToDefinition={returnPlanToDefinition}
          userRoles={userRoles}
          loadingUserRoles={loadingUserRoles}
          outOfTimeRequestStatus={outOfTimeRequestStatus}
          loadingOutOfTimeRequestStatus={loadingOutOfTimeRequestStatus}
          submittingOutOfTimeRequest={submittingOutOfTimeRequest}
          fetchOutOfTimeRequestStatus={fetchOutOfTimeRequestStatus}
          submitOutOfTimeRequest={submitOutOfTimeRequest}
        />
      </Box>
      <Dialog
        open={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {reopenTokenInfo?.action === 'reopenSelfAssessment'
              ? t('annualPlanning.reopenSelfAssessmentRequest')
              : t('annualPlanning.reopenDefinitionRequest')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              bgcolor: '#e8f5f5',
              borderRadius: 1,
              p: 2.5,
              mb: 2,
              border: '1px solid #00BCE4',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#00BCE4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Info sx={{ color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#00363a' }}>
                {reopenTokenInfo?.action === 'reopenSelfAssessment'
                  ? t('annualPlanning.reopenSelfAssessmentRequest')
                  : t('annualPlanning.reopenDefinitionRequest')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#004d52', lineHeight: 1.5 }}>
                {reopenTokenInfo?.action === 'reopenSelfAssessment'
                  ? t('annualPlanning.reopenSelfAssessmentRequestMessage')
                  : t('annualPlanning.reopenDefinitionRequestMessage')}
              </Typography>
              {reopenTokenInfo && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#006064', display: 'block' }}>
                    {reopenTokenInfo.versionId && `Version ID: ${reopenTokenInfo.versionId}`}
                    {reopenTokenInfo.fulfillmentVersionId &&
                      ` Fulfillment Version ID: ${reopenTokenInfo.fulfillmentVersionId}`}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" sx={{ mt: 2, color: '#004d52' }}>
                {t('annualPlanning.confirmReopenAction')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button
            onClick={() => handleDecision('reject')}
            disabled={isAnyLoading || processingReopenDecision}
            color="error"
            variant="outlined"
            sx={{
              px: 3,
              borderRadius: 20,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.reject')}
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => setShowReopenModal(false)}
              disabled={isAnyLoading || processingReopenDecision}
              variant="outlined"
              sx={{
                px: 3,
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#00BCE4',
                color: '#00BCE4',
                '&:hover': { borderColor: '#00acc1', bgcolor: 'rgba(0,188,212,0.04)' },
              }}
            >
              {t('common.close')}
            </Button>
            <Button
              onClick={() => handleDecision('accept')}
              color="primary"
              variant="contained"
              disabled={isAnyLoading || processingReopenDecision}
              sx={{
                px: 3,
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#00BCE4',
                '&:hover': { bgcolor: '#00acc1' },
              }}
            >
              {t('common.approve')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <OutOfTimeDecisionDialog
        open={showOutOfTimeModal}
        onClose={() => {
          setShowOutOfTimeModal(false);
          setOutOfTimeRejectionReason('');
        }}
        isSubmitting={isAnyLoading || processingOutOfTimeDecision}
        onDecision={handleOutOfTimeDecision}
        rejectionReason={outOfTimeRejectionReason}
        setRejectionReason={setOutOfTimeRejectionReason}
      />
    </Box>
  );
};

export default AnnualPlanning;
